import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 进度条组件
 * 提供统一的进度指示UI，支持多种样式和动画效果
 *
 * @example
 * ```tsx
 * // 基本用法
 * <ProgressBar value={60} />
 *
 * // 带标签的进度条
 * <ProgressBar
 *   value={75}
 *   label="积分使用进度"
 *   showValue={true}
 * />
 *
 * // 使用不同变体
 * <ProgressBar value={90} variant="success" />
 * <ProgressBar value={20} variant="danger" />
 *
 * // 不确定状态（加载中）
 * <ProgressBar indeterminate />
 * ```
 */
import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
const progressBarVariants = cva('rounded-full transition-all duration-300 ease-out', {
    variants: {
        variant: {
            default: 'bg-interactive-emerald-flash',
            primary: 'bg-interactive-cyan',
            success: 'bg-positive-lime',
            warning: 'bg-warning-amber',
            danger: 'bg-destructive-scarlet',
        },
        size: {
            small: 'h-1',
            medium: 'h-2',
            large: 'h-3',
        },
        animated: {
            true: '',
            false: 'transition-none',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'medium',
        animated: true,
    },
});
const defaultValueFormatter = (value, max) => {
    const percentage = Math.round((value / max) * 100);
    return `${percentage}%`;
};
/**
 * 进度条组件
 *
 * 用于显示任务或操作的完成进度
 */
export const ProgressBar = forwardRef(({ value = 0, max = 100, variant, size, animated, indeterminate = false, showValue = false, label, description, valueFormatter = defaultValueFormatter, className, ...props }, ref) => {
    const percentage = indeterminate ? 100 : Math.min(Math.max((value / max) * 100, 0), 100);
    const trackSize = {
        small: 'h-1',
        medium: 'h-2',
        large: 'h-3',
    };
    const trackClasses = cn('w-full bg-gray-200 rounded-full overflow-hidden', trackSize[size || 'medium']);
    // 注意：不确定状态的动画比简单的 `animate-pulse` 更复杂，
    // 因此暂时保留内联样式来实现左右滑动的效果。
    // 理想情况下，这应该在 tailwind.config.js 中定义为一个自定义动画。
    const indeterminateStyles = indeterminate
        ? {
            width: '40%',
            animation: 'indeterminate-progress 1.5s ease-in-out infinite',
        }
        : {
            width: `${percentage}%`,
        };
    // 模拟一个关键帧动画，因为我们不能修改 tailwind.config.js
    const keyframes = `
      @keyframes indeterminate-progress {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(250%); }
      }
    `;
    return (_jsxs("div", { ref: ref, className: cn('w-full', className), ...props, children: [_jsx("style", { children: indeterminate ? keyframes : '' }), (label || showValue) && (_jsxs("div", { className: "flex justify-between items-center mb-2", children: [label && _jsx("span", { className: "text-sm font-medium text-gray-700", children: label }), showValue && !indeterminate && (_jsx("span", { className: "text-sm text-gray-500", children: valueFormatter(value, max) }))] })), _jsx("div", { className: trackClasses, role: "progressbar", "aria-valuenow": indeterminate ? undefined : value, "aria-valuemin": 0, "aria-valuemax": max, "aria-label": label, children: _jsx("div", { className: progressBarVariants({ variant, size, animated }), style: indeterminateStyles }) }), description && _jsx("div", { className: "mt-1 text-xs text-gray-500", children: description })] }));
});
ProgressBar.displayName = 'ProgressBar';
export default ProgressBar;
//# sourceMappingURL=ProgressBar.js.map